import configurationServices from './services';
import { createFormData } from 'hooks';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import {
    call,
    put,
    select,
    takeEvery
} from 'redux-saga/effects';
import {
    configurationActions,
    parseAbsenceRate,
    parseAbsenceRateSchedules
} from './';

const parseIntegerValue = value => isEmpty(value) ? null : Number(value);

function* _getAbsenceRateConfiguration ({ payload }) {
    try {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(true));
        const { data } = yield call(configurationServices.fetchAbsenceRateConfiguration, null, payload);
        yield put(configurationActions.setConfigurationAbsenceRate(parseAbsenceRate(data)));
    } catch (e) {

    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(false));
    }
}

function* _getConfigurations () {
    try {
        const { data: { general } } = yield call(configurationServices.fetchConfigurations);
        const configurations = {};
        general.forEach(({ name, value }) => configurations[name] = value);
        const parsedConfigurations = {
            absence_rate_method: configurations.absence_rate_method,
            appointment_rate: configurations.appointment_rate,
            call_rate: configurations.call_rate,
            first_call_rate: configurations.first_call_rate,
            record: !!parseIntegerValue(configurations.record)
        };
        yield put(configurationActions.setConfigurationConfigurations(parsedConfigurations));
    } catch (e) {

    }
}

function* _getAbsenceRateSchedules () {
    try {
        const { configuration: { schedules } } = yield select();
        if (!isEmpty(schedules)) return;
        yield put(configurationActions.setConfigurationAbsenceRateLoading(true));
        const { data } = yield call(configurationServices.fetchSchedules);
        yield put(configurationActions.setConfigurationSchedules(parseAbsenceRateSchedules(data)));
    } catch (e) {

    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(false));
    }
}

function* _updateGlobalConfig ({ payload }) {
    try {
        const {
            appointment_rate,
            call_rate,
            first_call_rate,
            record
        } = payload;
        if (record !== undefined) {
            yield call(configurationServices.setRecordingFlag, null, { enable: record });
        }
        if (appointment_rate !== undefined && call_rate !== undefined && first_call_rate !== undefined) {
            yield call(configurationServices.putAdminRates, null, { appointment_rate, call_rate, first_call_rate });
        }
        yield put(configurationActions.setConfigurationResult(true));
        yield put(configurationActions.getConfigurations());
    } catch (e) {
        yield put(configurationActions.setConfigurationError(i18n.t('La configuración no pudo ser actualizada')));
    }
}

function* _uploadFile ({ payload: { file, option } }) {
    try {
        yield put(configurationActions.setConfigurationUploading(true));
        yield call(configurationServices.uploadFile, { option }, creteFormData({
            file
        }));
    } catch (e) {
        
    } finally {
        yield put(configurationActions.setConfigurationUploading(false));
    }
}

function* _uploadLogoFile ({ payload: { file } }) {
    try {
        yield put(configurationActions.setConfigurationUploadingLogo(true));
        yield put(configurationActions.setConfigurationUploadingLogoProcessing(true));
        yield call(configurationServices.uploadLogoFile, null, createFormData({
            logo: file
        }));
    } catch (e) {
        const errors = e?.response?.data?.logo;
        if (!!errors) {
            if (typeof errors[0] === 'string') {
                yield put(configurationActions.setConfigurationUploadingLogoError(i18n.t('El logo debe ser una imagen')));
            } else {
                const data = errors[0];
                const { IMAGE_DIMENSIONS_INVALID: { expected_max_dimensions: { max_height, max_width } } } = data ?? {};
                yield put(configurationActions.setConfigurationUploadingLogoError(
                    `${ i18n.t('Las dimensiones de la imagen deben ser de un máximo de') } ${ max_height } x ${ max_width }`
                ));
            }
        }
    } finally {
        yield put(configurationActions.setConfigurationUploadingLogo(false));
        yield put(configurationActions.setConfigurationUploadingLogoProcessing(false));
    }
}

function* _upsertAbsenceRateConfiguration ({ payload }) {
    try {
        yield put(configurationActions.setConfigurationAbsenceRateProcessing(true));
        yield call(configurationServices.postAbsenceRate, null, payload);
        yield put(configurationActions.setConfigurationAbsenceRateUpserted(true));
    } catch (e) {

    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateProcessing(false));
    }
    
}

function* _upsertAbsenceRateSchedule ({ payload: { name } }) {
    try {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(true));
        const { configuration: { schedules } } = yield select();
        const { data, data: { id: scheduleId, name: scheduleName } } = yield call(configurationServices.postScheduleConfig, null, { name });
        yield put(configurationActions.setConfigurationSchedules(orderBy([...schedules, { ...data, key: scheduleId, label: scheduleName }], ['key'], ['asc'])));
        yield put(configurationActions.setConfigurationScheduleCreation(true));
    } catch (e) {
        const data = e?.response?.data;
        if (data?.name && data?.name[0] === 'IS_ALREADY_TAKEN') yield put(configurationActions.setConfigurationScheduleCreationError(i18n.t('Ya utilizado')));
    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(false));
    }
}

function* _upsertAbsenceRateScheduleConfig ({ payload: { scheduleUpdated: { id, name }, ...payload } }) {
    try {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(true));
        const { configuration: { schedules } } = yield select();
        if (!!id) {
            const { data, data: { id: scheduleId, name: scheduleName } } = yield call(configurationServices.putScheduleConfig, { id }, { name });
            const newList = schedules.filter(({ key }) => scheduleId !== key);
            yield put(configurationActions.setConfigurationSchedules(orderBy([...newList, { ...data, key: scheduleId, label: scheduleName }], ['key'], ['asc'])));
        }
        const data = {
            ...payload,
            ...(!!id && { schedule_id: id })
        };
        yield call(configurationServices.postSchedule, null, data);
    } catch (e) {
        const data = e?.response?.data;
        if (data?.name && data?.name[0] === 'IS_ALREADY_TAKEN') yield put(configurationActions.setConfigurationScheduleUpdateError(i18n.t('Ya utilizado')));
    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateLoading(false));
    }
}

function* _upsertSpecialDay ({ payload }) {
    try {
        yield put(configurationActions.setConfigurationAbsenceRateProcessing(true));
        yield call(configurationServices.postSpecialDay, null, payload);
        const { configuration: { absenceRate, absenceRate: { special_days } } } = yield select();
        const { day, hours } = payload;
        const newSpecialDays = hours === null
            ? special_days.filter(({ date }) => date !== day)
            : [...special_days, { date: day, hours }];
        const newAbsenceRate = {
            ...absenceRate,
            special_days: newSpecialDays
        };
        yield put(configurationActions.setConfigurationAbsenceRate(newAbsenceRate));
        yield put(configurationActions.setConfigurationAbsenceRateUpserted(true));
    } catch (e) {

    } finally {
        yield put(configurationActions.setConfigurationAbsenceRateProcessing(false));
    }
    
}

export default [
    takeEvery(configurationActions.getAbsenceRateConfiguration.type, _getAbsenceRateConfiguration),
    takeEvery(configurationActions.getConfigurations.type, _getConfigurations),
    takeEvery(configurationActions.getAbsenceRateSchedules.type, _getAbsenceRateSchedules),
    takeEvery(configurationActions.updateGlobalConfig.type, _updateGlobalConfig),
    takeEvery(configurationActions.uploadFile.type, _uploadFile),
    takeEvery(configurationActions.uploadLogoFile.type, _uploadLogoFile),
    takeEvery(configurationActions.upsertAbsenceRateConfiguration.type, _upsertAbsenceRateConfiguration),
    takeEvery(configurationActions.upsertAbsenceRateSchedule.type, _upsertAbsenceRateSchedule),
    takeEvery(configurationActions.upsertAbsenceRateScheduleConfig.type, _upsertAbsenceRateScheduleConfig),
    takeEvery(configurationActions.upsertSpecialDay.type, _upsertSpecialDay)
];
